import dayjs from "dayjs";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useLazyGetLegalEntityQuery } from "src/shared/api/legalEntity";
import { ECountries } from "src/shared/types/common.types";
import { EOrganizationValue } from "src/shared/types/companies.types";
import { IPartnerFormValues } from "../types";
import { IEditPartnerFormValues } from "../types/partner";

export function useGetLegalEntityInfo() {
  const { partner_id } = useParams();
  const isEdit = Boolean(partner_id);
  const { getValues, setValue } = useFormContext<
    IPartnerFormValues | IEditPartnerFormValues
  >();

  const [getInfo, { isFetching: isLoading }] = useLazyGetLegalEntityQuery();

  const handleChangeInput = (val: string) => {
    const [
      addressesArray,
      isPostAddressSameAsLegal,
      registration_country_id,
      organization_form,
    ] = getValues([
      "addresses",
      "isPostAddressTheSame",
      "partner.registration_country_id",
      "partner.organization_form",
    ]);
    const patterns = {
      [ECountries.RU]:
        organization_form === EOrganizationValue.LegalEntity
          ? /^[0-9]{10}$/
          : /^[0-9]{12}$/,
      [ECountries.BY]: /^[0-9]{9}$/,
    };
    if (organization_form === EOrganizationValue.Individual) {
      return;
    }
    if (!registration_country_id) {
      return;
    }
    if (!patterns[registration_country_id]?.test(val)) {
      return;
    }

    const legalAddressIndex = addressesArray?.findIndex(
      (address) => address.type === "legal",
    );
    const postalAddressIndex = addressesArray?.findIndex(
      (address) => address.type === "postal",
    );

    getInfo({
      country_id: String(registration_country_id),
      registration_number: val,
    })
      .unwrap()
      .then((response) => {
        const { certificate, name, okpo, ogrn, ownership_id, address } =
          response || {};
        const {
          city,
          address: addressValue,
          postcode,
          region,
          area,
          country_id,
        } = address || {};
        const { issue_by, issued_at, number } = certificate || {};
        if (name) {
          setValue("partner.name", name, { shouldValidate: true });
        }
        if (okpo) {
          setValue("partner.okpo", okpo, { shouldValidate: true });
        }
        if (ogrn) {
          setValue("partner.ogrn", ogrn, { shouldValidate: true });
        }
        if (ownership_id) {
          setValue("partner.ownership_id", Number(ownership_id), {
            shouldValidate: true,
          });
        }

        if (issue_by) {
          setValue("license.issued_by", issue_by, {
            shouldValidate: true,
          });
        }
        if (issued_at) {
          setValue("license.issued_date", dayjs(issued_at), {
            shouldValidate: true,
          });
        }
        if (number) {
          setValue("license.number", number, {
            shouldValidate: true,
          });
        }
        // during edit partner action we can't change addresses from the partner form
        // - there are separate modals for this
        if (isEdit) {
          return;
        }

        if (postcode) {
          setValue(`addresses.${legalAddressIndex}.postcode`, postcode, {
            shouldValidate: true,
          });
          if (!isPostAddressSameAsLegal) {
            setValue(`addresses.${postalAddressIndex}.postcode`, postcode, {});
          }
        }
        if (country_id) {
          setValue(`addresses.${legalAddressIndex}.country_id`, country_id, {
            shouldValidate: true,
          });
          if (!isPostAddressSameAsLegal) {
            setValue(`addresses.${postalAddressIndex}.country_id`, country_id, {
              shouldValidate: true,
            });
          }
        }
        if (city !== "Undefined") {
          setValue(`addresses.${legalAddressIndex}.city`, city, {
            shouldValidate: true,
          });
          if (!isPostAddressSameAsLegal) {
            setValue(`addresses.${postalAddressIndex}.city`, city, {
              shouldValidate: true,
            });
          }
        }
        if (region) {
          setValue(`addresses.${legalAddressIndex}.region`, region, {
            shouldValidate: true,
          });
          if (!isPostAddressSameAsLegal) {
            setValue(`addresses.${postalAddressIndex}.region`, region, {
              shouldValidate: true,
            });
          }
        }
        if (area) {
          setValue(`addresses.${legalAddressIndex}.area`, area, {
            shouldValidate: true,
          });
          if (!isPostAddressSameAsLegal) {
            setValue(`addresses.${postalAddressIndex}.area`, area, {
              shouldValidate: true,
            });
          }
        }
        if (addressValue) {
          setValue(`addresses.${legalAddressIndex}.address`, addressValue, {
            shouldValidate: true,
          });
          if (!isPostAddressSameAsLegal) {
            setValue(`addresses.${postalAddressIndex}.address`, addressValue, {
              shouldValidate: true,
            });
          }
        }
      })
      .catch((error) => {
        const errorMessage = error.data?.error || "Произошла ошибка";
        enqueueSnackbar(errorMessage, {
          variant: "warning",
          handleClose: closeSnackbar,
        });
      });
  };

  return { isLoading, handleChangeInput };
}
